import React from "react";
import { usePlatform } from "../context/PlatformContext";
import { Sparkles } from "lucide-react";

/**
 * PreviewModeBanner — subtle but clear sitewide notice that buying/bidding is
 * not live yet. Only renders when admin has flipped preview_mode ON.
 *
 * Rendered globally inside <Layout>. Premium, calm, non-alarming styling —
 * dark surface with thin gold accent, not a red error banner.
 */
export default function PreviewModeBanner() {
  const { preview_mode, preview_notice, public_auctions_enabled, loaded } = usePlatform();
  if (!loaded || !preview_mode) return null;
  // When public auctions are hidden for compliance, swap the default copy to
  // a neutral phrasing that doesn't surface bidding language to a reviewer.
  const fallback = public_auctions_enabled
    ? "CannaForge is in preview access. Buying and bidding will open soon."
    : "CannaForge is in preview access. Purchasing will open soon.";
  // If admin's custom notice still mentions bidding while public auctions
  // are hidden, suppress the custom copy and use the neutral fallback. This
  // keeps the public face consistent even if admin forgot to update the
  // notice text after toggling auction visibility off.
  const notice = (!public_auctions_enabled && preview_notice && /\bbid|bidding\b/i.test(preview_notice))
    ? fallback
    : (preview_notice || fallback);
  return (
    <div
      className="border-b border-[var(--gold)]/30 bg-[var(--surface)]"
      data-testid="preview-mode-banner"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap justify-center text-center">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--gold)" }} />
        <span
          className="text-[10px] font-mono uppercase tracking-[0.22em]"
          style={{ color: "var(--gold)" }}
        >
          Preview Access
        </span>
        <span className="text-xs sm:text-[13px]" style={{ color: "var(--text-2)" }}>
          {notice}
        </span>
      </div>
    </div>
  );
}
